import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Users, 
  DollarSign, 
  Share2, 
  Copy, 
  Check, 
  TrendingUp,
  Clock,
  CreditCard,
  ArrowRight,
  Gift,
  Percent,
  ExternalLink,
  Shield,
  AlertTriangle,
  Link2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateDashboard = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);

  const fetchWithAuth = async (endpoint, options = {}) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });
    if (!response.ok) throw new Error('Request failed');
    return response.json();
  };

  useEffect(() => {
    if (token) loadData();
  }, [token]);

  // Handle return from Stripe Connect onboarding
  useEffect(() => {
    const connectParam = searchParams.get('connect');
    if (connectParam === 'complete') {
      toast.success('Stripe Connect setup completed! Checking status...');
      if (token) loadConnectStatus();
    } else if (connectParam === 'refresh') {
      toast.info('Onboarding link expired. Click "Connect with Stripe" to try again.');
    }
  }, [searchParams, token]);

  const loadConnectStatus = async () => {
    try {
      const status = await fetchWithAuth('/api/affiliate/connect/status');
      setConnectStatus(status);
    } catch (error) {
      console.error('Failed to load connect status:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, referralsRes, earningsRes, payoutsRes, connectRes] = await Promise.all([
        fetchWithAuth('/api/affiliate/stats'),
        fetchWithAuth('/api/affiliate/referrals'),
        fetchWithAuth('/api/affiliate/earnings'),
        fetchWithAuth('/api/affiliate/payouts'),
        fetchWithAuth('/api/affiliate/connect/status').catch(() => null)
      ]);

      setStats(statsRes);
      setReferrals(referralsRes.referrals || []);
      setEarnings(earningsRes.earnings || []);
      setPayouts(payoutsRes.payouts || []);
      if (connectRes) setConnectStatus(connectRes);
    } catch (error) {
      console.error('Failed to load affiliate data:', error);
      toast.error('Failed to load affiliate data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (stats?.referral_link) {
      navigator.clipboard.writeText(stats.referral_link);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const requestPayout = async () => {
    const minPayout = stats?.minimum_payout || 50;
    if (stats?.available_for_payout < minPayout) {
      toast.error(`Minimum payout is $${minPayout}`);
      return;
    }

    setRequestingPayout(true);
    try {
      const paymentMethod = connectStatus?.onboarding_complete ? 'stripe_connect' : 'manual';
      await fetchWithAuth('/api/affiliate/payouts/request', {
        method: 'POST',
        body: JSON.stringify({
          payment_method: paymentMethod,
          payment_details: {}
        })
      });
      toast.success(paymentMethod === 'stripe_connect' 
        ? 'Payout requested! Funds will transfer automatically once approved.' 
        : 'Payout request submitted for manual processing.');
      loadData();
    } catch (error) {
      toast.error('Failed to submit payout request');
    } finally {
      setRequestingPayout(false);
    }
  };

  const startStripeConnect = async () => {
    setConnectLoading(true);
    try {
      const result = await fetchWithAuth('/api/affiliate/connect/onboard', { 
        method: 'POST',
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      if (result.onboarding_url) {
        window.location.href = result.onboarding_url;
      }
    } catch (error) {
      toast.error('Failed to start Stripe Connect setup');
    } finally {
      setConnectLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const result = await fetchWithAuth('/api/affiliate/connect/dashboard-link');
      if (result.url) {
        window.open(result.url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 py-8 px-4" data-testid="affiliate-dashboard">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-white">Affiliate Program</h1>
          <p className="text-slate-400">Earn 20% lifetime commission on Pro subscriptions</p>
        </div>

        {/* Referral Link Card */}
        <Card className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border-emerald-500/30">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm text-emerald-400 font-medium mb-2">Your Referral Link</p>
                <div className="flex items-center gap-2">
                  <Input 
                    value={stats?.referral_link || ''} 
                    readOnly 
                    className="bg-slate-900/50 border-slate-700 text-white font-mono text-sm"
                    data-testid="referral-link-input"
                  />
                  <Button 
                    onClick={copyReferralLink} 
                    variant="outline"
                    className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10"
                    data-testid="copy-referral-btn"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-sm text-slate-400">Your Code</p>
                <p className="text-2xl font-bold text-emerald-400 font-mono">{stats?.referral_code}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="py-4 text-center">
              <Users className="h-8 w-8 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.total_referrals || 0}</p>
              <p className="text-xs text-slate-400">Total Referrals</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="py-4 text-center">
              <TrendingUp className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.pro_conversions || 0}</p>
              <p className="text-xs text-slate-400">Pro Conversions</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="py-4 text-center">
              <Percent className="h-8 w-8 text-amber-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.conversion_rate || 0}%</p>
              <p className="text-xs text-slate-400">Conversion Rate</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="py-4 text-center">
              <DollarSign className="h-8 w-8 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">${stats?.total_earned?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-slate-400">Total Earned</p>
            </CardContent>
          </Card>
        </div>

        {/* Earnings Summary & Payout */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-400" />
                Earnings Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-slate-800">
                <span className="text-slate-400">Total Earned</span>
                <span className="text-white font-semibold">${stats?.total_earned?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-800">
                <span className="text-slate-400">Already Paid Out</span>
                <span className="text-slate-300">${stats?.paid_out?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-800">
                <span className="text-slate-400 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Held ({stats?.hold_days || 30}-day hold)
                </span>
                <span className="text-amber-400">${stats?.held_earnings?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-800">
                <span className="text-slate-400">Available Earnings</span>
                <span className="text-slate-300">${stats?.pending_earnings?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-emerald-400 font-medium">Available for Payout</span>
                <span className="text-emerald-400 font-bold text-lg">${stats?.available_for_payout?.toFixed(2) || '0.00'}</span>
              </div>
              {(stats?.held_earnings || 0) > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
                  <Shield className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-300">
                    Commissions are held for {stats?.hold_days || 30} days before becoming available for payout. This protects against chargebacks.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="space-y-6">
            {/* Stripe Connect Card */}
            <Card className={`border ${connectStatus?.onboarding_complete 
              ? 'bg-emerald-900/20 border-emerald-500/30' 
              : 'bg-slate-900 border-slate-800'}`}
            >
              <CardContent className="py-5">
                {connectStatus?.onboarding_complete ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                        <Check className="h-5 w-5 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Stripe Connected</p>
                        <p className="text-xs text-slate-400">Payouts transfer automatically</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={openStripeDashboard}
                      className="border-slate-700 text-slate-300"
                      data-testid="stripe-dashboard-btn"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" /> Dashboard
                    </Button>
                  </div>
                ) : connectStatus?.connected && !connectStatus?.onboarding_complete ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Onboarding Incomplete</p>
                        <p className="text-xs text-slate-400">Complete your Stripe setup to enable auto-payouts</p>
                      </div>
                    </div>
                    <Button 
                      onClick={startStripeConnect}
                      disabled={connectLoading}
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="complete-stripe-btn"
                    >
                      {connectLoading ? 'Loading...' : 'Complete Setup'}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center">
                        <Link2 className="h-5 w-5 text-indigo-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Connect with Stripe</p>
                        <p className="text-xs text-slate-400">Enable automatic payouts to your bank</p>
                      </div>
                    </div>
                    <Button 
                      onClick={startStripeConnect}
                      disabled={connectLoading}
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="connect-stripe-btn"
                    >
                      {connectLoading ? 'Loading...' : 'Connect'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Request Payout Card */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-blue-400" />
                  Request Payout
                </CardTitle>
                <CardDescription>Minimum payout: ${stats?.minimum_payout?.toFixed(0) || '50'}.00</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <p className="text-slate-400 text-sm mb-1">Available Balance</p>
                  <p className="text-3xl font-bold text-emerald-400">${stats?.available_for_payout?.toFixed(2) || '0.00'}</p>
                </div>
                <Button 
                  onClick={requestPayout}
                  disabled={requestingPayout || (stats?.available_for_payout || 0) < (stats?.minimum_payout || 50)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="request-payout-btn"
                >
                  {requestingPayout ? 'Submitting...' : (
                    connectStatus?.onboarding_complete 
                      ? 'Request Auto-Payout' 
                      : 'Request Manual Payout'
                  )}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  {connectStatus?.onboarding_complete 
                    ? 'Funds transfer automatically to your Stripe account once approved'
                    : 'Manual payouts are processed within 5-7 business days'
                  }
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-800 pb-2">
          {['overview', 'referrals', 'earnings', 'payouts'].map(tab => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'ghost'}
              onClick={() => setActiveTab(tab)}
              className={activeTab === tab ? 'bg-emerald-600' : 'text-slate-400'}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">How It Works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center space-y-2">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                    <Share2 className="h-6 w-6 text-blue-400" />
                  </div>
                  <h3 className="text-white font-semibold">1. Share Your Link</h3>
                  <p className="text-sm text-slate-400">Share your unique referral link with friends, on social media, or your website</p>
                </div>
                <div className="text-center space-y-2">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                    <Users className="h-6 w-6 text-emerald-400" />
                  </div>
                  <h3 className="text-white font-semibold">2. They Sign Up</h3>
                  <p className="text-sm text-slate-400">When someone signs up using your link, they're tracked as your referral</p>
                </div>
                <div className="text-center space-y-2">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto">
                    <Gift className="h-6 w-6 text-amber-400" />
                  </div>
                  <h3 className="text-white font-semibold">3. Earn Forever</h3>
                  <p className="text-sm text-slate-400">Earn 20% of every Pro subscription payment they make - for life!</p>
                </div>
              </div>
              
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mt-6">
                <p className="text-emerald-400 font-medium text-center">
                  Example: Refer 10 users who subscribe to Pro Monthly ($9.99/mo) = <span className="text-xl font-bold">$19.98/month</span> passive income!
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'referrals' && (
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Your Referrals</CardTitle>
              <CardDescription>{referrals.length} total referrals</CardDescription>
            </CardHeader>
            <CardContent>
              {referrals.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No referrals yet. Share your link to get started!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {referrals.map(ref => (
                    <div key={ref.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{ref.referred_username}</p>
                        <p className="text-xs text-slate-400">{ref.referred_email}</p>
                      </div>
                      <div className="text-right">
                        {ref.converted_to_pro ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400">Pro User</Badge>
                        ) : (
                          <Badge className="bg-slate-700 text-slate-300">Free User</Badge>
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'earnings' && (
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Earnings History</CardTitle>
              <CardDescription>{earnings.length} commission records</CardDescription>
            </CardHeader>
            <CardContent>
              {earnings.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No earnings yet. When your referrals upgrade to Pro, you'll see commissions here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {earnings.map(earning => (
                    <div key={earning.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{earning.referred_username}</p>
                        <p className="text-xs text-slate-400">
                          {earning.subscription_period === 'monthly' ? 'Monthly' : 'Annual'} subscription
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 font-bold">+${earning.commission_amount.toFixed(2)}</p>
                        <p className="text-xs text-slate-400">
                          {earning.paid_out ? 'Paid' : 'Pending'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'payouts' && (
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Payout History</CardTitle>
              <CardDescription>{payouts.length} payout requests</CardDescription>
            </CardHeader>
            <CardContent>
              {payouts.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No payout requests yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payouts.map(payout => (
                    <div key={payout.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div>
                        <p className="text-white font-medium">${payout.amount.toFixed(2)}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(payout.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge className={
                        payout.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                        payout.status === 'approved' ? 'bg-blue-500/20 text-blue-400' :
                        payout.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                        'bg-amber-500/20 text-amber-400'
                      }>
                        {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AffiliateDashboard;
